import type { FieldDefinition } from "@estelle/domain";

import {
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "@/modules/tasks/task-enums";

/**
 * The fields the Tasks module offers to the filter builder, the column
 * chooser and the sort control.
 *
 * Order matters — the column chooser takes the first six as the default
 * set. Reference, title, status, priority, assignee and due date are what
 * tells one task from another at a glance; project, reporter, tags, linked
 * entity type and overdue are the situational narrowing FR-TSK-16 asks for
 * on top of that.
 */
export const TASK_FIELDS: FieldDefinition[] = [
  { name: "reference", label: "Reference", type: "text", sortable: true },
  { name: "title", label: "Title", type: "text", sortable: true },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: TASK_STATUS_OPTIONS,
  },
  {
    name: "priority",
    label: "Priority",
    type: "enum",
    sortable: true,
    options: TASK_PRIORITY_OPTIONS,
  },
  { name: "assigneeUserId", label: "Assignee", type: "reference" },
  { name: "dueDate", label: "Due date", type: "date", sortable: true },
  { name: "projectId", label: "Project", type: "reference" },
  { name: "reporterUserId", label: "Reporter", type: "reference" },
  { name: "tags", label: "Tags", type: "tag" },
  /**
   * FR-TSK-16's "linked entity type". Declared because the requirement names
   * it, but there is nothing behind it yet: `TasksRepository.list` returns
   * `Task`, not `TaskWithDetails`, so the list row this module's own
   * `projectTask` reads from carries no `links` array at all — the same gap
   * Orders documents for `sku` and `warehouse` in `ORDER_FIELDS`. Closing it
   * needs the adapter to grow a row-level join (or the interface to return a
   * richer row), not a screen reaching past what `list()` promises to get at
   * data that was never returned.
   */
  { name: "linkedEntityType", label: "Linked to", type: "tag" },
  /**
   * FR-TSK-16's overdue filter. Unlike `linkedEntityType`, this one *is*
   * computable from `Task` alone — `dueDate` and `status` are both on the
   * row — so `projections.ts` fills it in, and `TaskList.tsx` and
   * `TaskBoard.tsx` filter and sort against it client-side rather than
   * through `TasksRepository.list`'s `filter`, because the shared fixture
   * adapter's own filtering projection (in `adapters/fixture/index.ts`,
   * outside this module's scope to change) does not expose it either.
   */
  { name: "overdue", label: "Overdue", type: "boolean" },
];

/**
 * The fields the Projects screen offers.
 *
 * Six, and there is nothing left over — a project record is `name`, `key`,
 * an owner, a description and two booleans/dates worth showing, the same
 * "exactly six, nothing optional" shape `WAREHOUSE_FIELDS` documents.
 */
export const PROJECT_FIELDS: FieldDefinition[] = [
  { name: "name", label: "Name", type: "text", sortable: true },
  { name: "key", label: "Key", type: "text", sortable: true },
  { name: "ownerUserId", label: "Owner", type: "reference" },
  { name: "description", label: "Description", type: "text" },
  { name: "isArchived", label: "Archived", type: "boolean" },
  { name: "createdAt", label: "Created", type: "date", sortable: true },
];

/**
 * What the Recurring section offers (#624). One row is a schedule, not a task,
 * so it has its own vocabulary, as Projects does. `repeats` is the sentence a
 * person reads ("Every Saturday at 10:00"); `lastProblem` is why the latest
 * occurrence made nothing, empty when it went well.
 */
export const RECURRING_FIELDS: FieldDefinition[] = [
  { name: "title", label: "Title", type: "text", sortable: true },
  { name: "repeats", label: "Repeats", type: "text" },
  { name: "assigneeUserId", label: "Assignee", type: "reference" },
  { name: "nextDate", label: "Next due", type: "date", sortable: true },
  { name: "projectId", label: "Project", type: "reference" },
  { name: "isActive", label: "Active", type: "boolean" },
  {
    name: "priority",
    label: "Priority",
    type: "enum",
    sortable: true,
    options: TASK_PRIORITY_OPTIONS,
  },
  { name: "lastProblem", label: "Problem", type: "text" },
];
